/**
 * Menu Particle System
 *
 * Creates floating decorative particles that represent different zones.
 * Particles drift upward and react to device tilt for immersive effect.
 */

import Phaser from 'phaser';
import { Z_INDEX, ZONE_THEMES } from '@/config/designSystem';

export class MenuParticleSystem {
  private scene: Phaser.Scene;
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createParticleTextures();
    this.createZoneParticles();

    // Listen for tilt updates
    scene.events.on('tilt-update', this.onTiltUpdate, this);
  }

  /**
   * Create particle textures programmatically
   */
  private createParticleTextures(): void {
    // Create a simple circle texture for particles
    const graphics = this.scene.make.graphics({ x: 0, y: 0 }, false);

    // Main particle (circle)
    graphics.clear();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(8, 8, 8);
    graphics.generateTexture('menu-particle', 16, 16);

    // Star particle
    graphics.clear();
    graphics.fillStyle(0xffffff, 1);
    this.drawStar(graphics, 8, 8, 5, 8, 4);
    graphics.generateTexture('menu-star', 16, 16);

    // Sparkle particle
    graphics.clear();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(6, 6, 2);
    graphics.fillRect(3, 6, 6, 1);
    graphics.fillRect(6, 3, 1, 6);
    graphics.generateTexture('menu-sparkle', 12, 12);

    // Destroy graphics after generating textures
    graphics.destroy();
  }

  /**
   * Draw a star shape
   */
  private drawStar(
    graphics: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    spikes: number,
    outerRadius: number,
    innerRadius: number
  ): void {
    let rot = (Math.PI / 2) * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;

    graphics.beginPath();
    graphics.moveTo(cx, cy - outerRadius);

    for (let i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      graphics.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      graphics.lineTo(x, y);
      rot += step;
    }

    graphics.lineTo(cx, cy - outerRadius);
    graphics.closePath();
    graphics.fillPath();
  }

  /**
   * Create particle emitters for each zone
   */
  private createZoneParticles(): void {
    const { width, height } = this.scene.cameras.main;
    const zoneCount = Object.keys(ZONE_THEMES).length;
    const zoneHeight = height / zoneCount;

    // Create particles for each zone
    Object.values(ZONE_THEMES).forEach((zone, index) => {
      const yPos = height - index * zoneHeight;
      const yRange = zoneHeight;

      // Choose particle type based on zone
      let particleTexture = 'menu-particle';
      let particleColor = zone.accent;

      if (zone.id === 4 || zone.id === 5) {
        // Starlit Night and Golden Summit - use stars/sparkles
        particleTexture = index % 2 === 0 ? 'menu-star' : 'menu-sparkle';
      }

      // Create emitter for this zone
      const emitter = this.scene.add.particles(0, 0, particleTexture, {
        x: { min: 0, max: width },
        y: { min: yPos - yRange / 2, max: yPos + yRange / 2 },
        speedY: { min: -20, max: -10 }, // Drift upward slowly
        speedX: { min: -10, max: 10 }, // Gentle horizontal drift
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.6, end: 0 },
        lifespan: 8000,
        frequency: 500, // Spawn every 500ms
        blendMode: Phaser.BlendModes.ADD,
        tint: particleColor,
      });

      emitter.setDepth(Z_INDEX.particles);
      emitter.setScrollFactor(0);

      this.emitters.push(emitter);
    });
  }

  /**
   * Handle tilt updates - particles drift with tilt
   */
  private onTiltUpdate(angle: number): void {
    // Adjust particle gravity based on tilt
    this.emitters.forEach(emitter => {
      emitter.gravityX = angle * 5; // Horizontal drift based on tilt
      emitter.gravityY = -10 + Math.abs(angle) * 0.5; // Upward with slight variation
    });
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.scene.events.off('tilt-update', this.onTiltUpdate, this);
    this.emitters.forEach(emitter => emitter.destroy());
    this.emitters = [];
  }
}
